// @ts-strict-ignore
import { Injectable } from '@angular/core';
import deepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { ECONOMIC_CASE_EXTENSION_LENGTH } from '../../../app.constants';
import { getLengthOfTimeline } from '../../../functions/timeline/get-length-of-timeline';
import { fitWeightCurveToPeriod } from '../../../functions/transition-targets/fit-weight-curve-to-period';
import { getWeightCurve } from '../../../functions/transition-targets/get-weight-curve';
import { TrajectoryFunctionType } from '../../../functions/transition-targets/trajectory-function-type';
import { BoardParametersV2 } from '../../../model/board-parameters';
import { BoardTimeline } from '../../../model/board-timeline';
import { EmissionFactorCalculationMethod } from '../../../model/enums/emission-factor-calculation-method';
import { TransitionElement } from '../../../model/enums/transition-element';
import { TargetWeights } from '../../../model/target-balancing/target-weights';
import {
  CccAlgorithmService,
  DEPLOYED_BUT_NOT_COMPATIBLE_WITH_ALGORITHM,
} from '../ccc-algorithm/ccc-algorithm.service';
import { KeyMap } from '../ccc-algorithm/key-map';
import { ELECTRICITY } from '../ccc-algorithm/resource/resource';
import { CCCModelUtility } from '../ccc-model-utility/ccc-model-utility';
import { CalculationResult } from '../model/calculation-result';
import { ClimateCortexOptions } from '../model/climate-cortex-options';
import { CccAdapterTransitionTargetItem } from './ccc-adapter-transition-target-item';

/**
 * TODOs
 * - Allow for adjusted weights (after ccc-algorithm-service has processed them) to be reported back
 **/

@Injectable({
  providedIn: 'root',
})
export class CccAdapterService {
  private _isInitialised: boolean = false; // Keep track of which board this is initialised for?
  private _inventoriesTransitionElementAllParameters: { [inventoryId: string]: BoardParametersV2 } | undefined;
  private _inventoryIdMap: { [year: number]: string } = {};
  private _options: ClimateCortexOptions;
  private _resultCache: CalculationResult[];
  private _resultCacheWithEndYearWeights: CalculationResult[];
  private _timeline: BoardTimeline;
  private _transitionElementAllParameters: BoardParametersV2;
  private _transitionElementItems: CccAdapterTransitionTargetItem[];
  private _transitionWeights: KeyMap<number[]> = {};

  constructor(
    private cccAlgorithmService: CccAlgorithmService,
    private _cccModelUtility: CCCModelUtility
  ) {}

  get isInitialised(): boolean {
    return this._isInitialised;
  }

  addTransitionElement(transitionElementItem: CccAdapterTransitionTargetItem): void {
    this._emptyCache();
    const itemToInsert: CccAdapterTransitionTargetItem = cloneDeep(transitionElementItem);
    const indexOfPreviousItem = this._transitionElementItems.findIndex(
      (t) => t.transitionElement === itemToInsert.transitionElement
    );
    indexOfPreviousItem !== -1
      ? this._transitionElementItems.splice(indexOfPreviousItem, 1, itemToInsert)
      : this._transitionElementItems.push(itemToInsert);
    this._init();
  }

  calculate(t: number, calculateCccCosts?: boolean, calculateCostsForTEs?: TransitionElement[]): CalculationResult;
  calculate(
    t: number,
    calculateCccCosts?: boolean,
    calculateCostsForTEs?: TransitionElement[],
    withEndYearWeights?: boolean
  ): { calculationResult: CalculationResult; endYearWeightsCalculationResult: CalculationResult };
  calculate(
    t: number,
    calculateCccCosts?: boolean,
    calculateCostsForTEs?: TransitionElement[],
    withEndYearWeights?: boolean
  ): CalculationResult | { calculationResult: CalculationResult; endYearWeightsCalculationResult: CalculationResult } {
    const shouldRecalculateCurrentYearWeightsResult =
      this._resultCache[t] === undefined || calculateCostsForTEs?.length;
    const shouldRecalculateEndYearWeightResult =
      withEndYearWeights && (this._resultCacheWithEndYearWeights[t] === undefined || calculateCostsForTEs?.length);
    if (shouldRecalculateCurrentYearWeightsResult) {
      this._calculateForT(
        t,
        Math.min(t, getLengthOfTimeline(this._timeline) - 1),
        calculateCccCosts,
        calculateCostsForTEs,
        this._resultCache
      );
    }
    if (shouldRecalculateEndYearWeightResult) {
      const endYearT = getLengthOfTimeline(this._timeline) - 1;
      this._calculateForT(t, endYearT, calculateCccCosts, calculateCostsForTEs, this._resultCacheWithEndYearWeights);
    }
    return !withEndYearWeights
      ? this._resultCache[t]
      : {
          calculationResult: this._resultCache[t],
          endYearWeightsCalculationResult: this._resultCacheWithEndYearWeights[t],
        };
  }

  calculateCompleteEconomicCase(calculateCostsForTEs?: TransitionElement[]): CalculationResult[] {
    const arr: CalculationResult[] = [];
    for (let t = 0; t < getLengthOfTimeline(this._timeline) + ECONOMIC_CASE_EXTENSION_LENGTH; t++) {
      arr.push(this.calculate(t, true, calculateCostsForTEs));
    }
    return arr;
  }

  getCccEnergyEfficiencyForResourceAndInventory(inventoryId: string, cccId: string, resourceId: string): number {
    return this.cccAlgorithmService.getCccEnergyEfficiencyForResourceAndInventoryId(inventoryId, cccId, resourceId);
  }

  getCccEnergyIntensityForInventory(inventoryId: string, cccId: string): number {
    return this.cccAlgorithmService.getCccEnergyIntensityForInventoryId(inventoryId, cccId);
  }

  getCccEmissionBau(t: number) {
    return this.cccAlgorithmService.getCccEmissionBau(t);
  }

  getCccEmissionsForYear(y: number) {
    return this.cccAlgorithmService.getCccEmissionsForInventoryId(this._inventoryIdMap[y]);
  }

  getCccEnergyConsumptionForYear(y: number) {
    return this.cccAlgorithmService.getCccResourceEnergyConsumptionForInventoryId(this._inventoryIdMap[y]);
  }

  getEmissionFactorsForInventoryYear(y: number): KeyMap<number> {
    return this.cccAlgorithmService.getEmissionFactorsForInventoryId(this._inventoryIdMap[y]);
  }

  getInitialPrimaryOutputDeliveredFromSupplyTE(transitionElement: TransitionElement): number | undefined {
    if (this._cccModelUtility.isTransitionElementSupplyAlteration(transitionElement)) {
      return this.cccAlgorithmService.getInitialPrimaryOutputDeliveredFromSupplyTE(transitionElement);
    }
    return undefined;
  }

  getInitialValueFromSupplyUpdateTE(transitionElement: TransitionElement): number | undefined {
    if (this._cccModelUtility.isTransitionElementSupplyUpdate(transitionElement)) {
      return this.cccAlgorithmService.getInitialValueFromSupplyUpdateTE(transitionElement);
    }
    return undefined;
  }

  init(
    options: ClimateCortexOptions,
    transitionTargetItems: CccAdapterTransitionTargetItem[],
    transitionElementAllParameters: BoardParametersV2,
    timeline: BoardTimeline,
    forceInit = false,
    inventoriesTransitionElementAllParameters?: {
      [inventoryId: string]: { inventoryYear: number; variables: BoardParametersV2 };
    }
  ): void {
    if (
      !deepEqual(this._transitionElementItems, transitionTargetItems) ||
      !deepEqual(this._transitionElementAllParameters, transitionElementAllParameters) ||
      !deepEqual(this._timeline, timeline) ||
      forceInit ||
      !deepEqual(this._inventoriesTransitionElementAllParameters, inventoriesTransitionElementAllParameters) ||
      !deepEqual(this._options, options)
    ) {
      this._emptyCache();
      this._transitionElementItems = cloneDeep(transitionTargetItems);
      this._transitionElementAllParameters = cloneDeep(transitionElementAllParameters);
      this._options = cloneDeep(options);

      if (inventoriesTransitionElementAllParameters !== undefined) {
        this._inventoriesTransitionElementAllParameters = this._cloneVariables(
          inventoriesTransitionElementAllParameters
        );
        this._inventoryIdMap = this._mapInventoryYearsToIds(inventoriesTransitionElementAllParameters);
      }
      this._timeline = cloneDeep(timeline);
      this._init();
    }
  }

  setEndYearWeight(teId: TransitionElement, weight: number): void {
    this._emptyCache();
    const transitionTargetItem = this._transitionElementItems.find((item) => item.transitionElement === teId);
    if (transitionTargetItem.trajectoryFunctionType === TrajectoryFunctionType.MANUAL) {
      this._transitionWeights[teId][getLengthOfTimeline(this._timeline) - 1] = weight;
    } else {
      this._setWeightCurve(transitionTargetItem, weight);
    }
  }

  setWeights(transitionTargetItem: CccAdapterTransitionTargetItem): void {
    this._emptyCache(); // Possible optimization: Check this._transitionWeights before and after next call to determine if cache needs to be emptied
    this._setWeights(transitionTargetItem, true);
  }

  private _calculateForT(
    t: number,
    weightsForT: number,
    calculateCccCosts: boolean,
    calculateCostsForTEs: TransitionElement[],
    resultCache: CalculationResult[]
  ) {
    //Possible optimisation: check if calculateCostsForTEs are exactly the same as used when constructing the cached result
    if (t !== 0 && resultCache[0] === undefined) {
      // in order to get correct emission factor BAU from supply resources, we need to have calculated year 0
      this.calculate(0);
    }
    const targetWeights: TargetWeights = {};
    this._transitionElementItems.forEach(
      (item) =>
        (targetWeights[item.transitionElement] = {
          value: this._transitionWeights[item.transitionElement][weightsForT],
        })
    );
    const tSetup: number = Math.min(t, getLengthOfTimeline(this._timeline) - 1);
    resultCache[t] = this.cccAlgorithmService.calculate(
      tSetup,
      t,
      targetWeights,
      calculateCccCosts,
      calculateCostsForTEs
    );
  }

  private _cloneVariables(inventoryWithVariables: {
    [inventoryId: string]: { inventoryYear: number; variables: BoardParametersV2 };
  }): { [inventoryId: string]: BoardParametersV2 } {
    return Object.entries(inventoryWithVariables).reduce<{ [inventoryId: string]: BoardParametersV2 }>(
      (acc, [id, value]) => {
        acc[id] = cloneDeep(value.variables);
        return acc;
      },
      {}
    );
  }

  private _emptyCache(): void {
    this._resultCache = new Array(getLengthOfTimeline(this._timeline));
    this._resultCacheWithEndYearWeights = new Array(getLengthOfTimeline(this._timeline));
  }

  private _emptyWeights(): void {
    this._transitionWeights = {};
  }

  private _getOverridingEmissionFactors(): KeyMap<string> | undefined {
    if (this._options.electricityEmissionFactorType === EmissionFactorCalculationMethod.ImportedGrid) {
      return { [ELECTRICITY]: 'emission_factor_electricity_current' };
    }
    return undefined;
  }

  private _init() {
    this._initCccAlgorithmService();
    this._emptyWeights();
    this._setAllWeights();
    this._isInitialised = true;
  }

  private _initCccAlgorithmService(): void {
    const transitionTargets = this._transitionElementItems
      .map((n) => n.transitionElement)
      .filter(
        // TODO remove filtering once all TTs `Deployed` are supported
        (transitionElement) => !DEPLOYED_BUT_NOT_COMPATIBLE_WITH_ALGORITHM.includes(transitionElement)
      );
    const overridingEmissionFactors: KeyMap<string> | undefined = this._getOverridingEmissionFactors();
    this.cccAlgorithmService.init(
      this._transitionElementAllParameters,
      transitionTargets,
      this._inventoriesTransitionElementAllParameters,
      overridingEmissionFactors
    );
  }

  private _mapInventoryYearsToIds(inventoriesTransitionElementAllParameters: {
    [inventoryId: string]: { inventoryYear: number; variables: BoardParametersV2 };
  }): { [year: number]: string } {
    return Object.entries(inventoriesTransitionElementAllParameters).reduce<{ [year: number]: string }>(
      (acc, [id, value]) => {
        acc[value.inventoryYear] = id;
        return acc;
      },
      {}
    );
  }

  private _setAllWeights(): void {
    this._transitionElementItems.forEach((t) => this._setWeights(t));
  }

  private _setWeights(transitionTargetItem: CccAdapterTransitionTargetItem, overwriteExisting = false): void {
    if (this._transitionWeights[transitionTargetItem.transitionElement] !== undefined && !overwriteExisting) {
      return;
    }
    if (transitionTargetItem.trajectoryFunctionType === TrajectoryFunctionType.MANUAL) {
      this._transitionWeights[transitionTargetItem.transitionElement] = fitWeightCurveToPeriod(
        transitionTargetItem.manualPath,
        getLengthOfTimeline(this._timeline)
      );
    } else {
      this._setWeightCurve(transitionTargetItem, transitionTargetItem.targetLevel);
    }
  }

  private _setWeightCurve(transitionTargetItem: CccAdapterTransitionTargetItem, weight: number) {
    let originValue: number;
    if (this._cccModelUtility.isTransitionElementSupplyAlteration(transitionTargetItem.transitionElement)) {
      originValue = this.cccAlgorithmService.getInitialPrimaryOutputDeliveredFromSupplyTE(
        transitionTargetItem.transitionElement
      );
    } else if (this._cccModelUtility.isTransitionElementSupplyUpdate(transitionTargetItem.transitionElement)) {
      originValue = this.cccAlgorithmService.getInitialValueFromSupplyUpdateTE(transitionTargetItem.transitionElement);
    }
    this._transitionWeights[transitionTargetItem.transitionElement] = getWeightCurve(
      weight,
      getLengthOfTimeline(this._timeline),
      transitionTargetItem.trajectoryFunctionType,
      originValue,
      this._cccModelUtility.isTransitionElementSupplyAlteration(transitionTargetItem.transitionElement) ||
        this._cccModelUtility.isTransitionElementSupplyUpdate(transitionTargetItem.transitionElement)
    );
  }
}
